import { useEffect, useState } from "react";
import api from "../api/api";

export default function Profile({ onLoginClick }) {
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({});
    const [bookings, setBookings] = useState([]);
    const [activeTab, setActiveTab] = useState("profile");
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);

    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(new Date());
    const [cancelLoading, setCancelLoading] = useState(null);

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 1000); // 🔥 every second

        return () => clearInterval(interval);
    }, []);



    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("user"));

        // If user is not logged in
        if (!storedUser) {
            setUser(null);
            setBookings([]);
            return;
        }

        setUser(storedUser);

        setForm({
            name: storedUser.name || "",
            email: storedUser.email || "",
            phone: storedUser.phone || "",
            nationality: storedUser.nationality || "",
        });

        fetchBookings(storedUser.id);
    }, []);

    const fetchBookings = async (userId) => {
        try {
            const res = await api.get(`/user/${userId}/bookings`);
            setBookings(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const updateProfile = async (e) => {
        e.preventDefault();

        try {
            const res = await api.put(`/user/${user.id}`, form);

            localStorage.setItem("user", JSON.stringify(res.data.user));
            setUser(res.data.user);

            alert("Profile updated successfully");
        } catch (err) {
            console.error(err);
            alert("Update failed");
        }
    };

    const formatDate = (date) =>
        new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });



    const cancelBooking = async (id) => {
        if (!window.confirm("Are you sure you want to cancel this booking?")) return;

        try {
            setCancelLoading(id);

            await api.post(`/booking/${id}/cancel`);

            alert("Booking cancelled");

            // refresh bookings
            fetchBookings(user.id);

        } catch (err) {
            alert(err.response?.data?.message || "Cancel failed");
        } finally {
            setCancelLoading(null);
        }
    };

    const openReviewModal = (booking) => {
        setSelectedBooking(booking);
        setShowReviewModal(true);
    };

    const submitReview = async () => {
        if (rating === 0) {
            alert("Please select a rating");
            return;
        }

        setLoading(true);

        try {
            if (selectedBooking.review) {
                // ✅ UPDATE
                await api.put(
                    `/reviews/${selectedBooking.review.id}`,
                    {
                        rating,
                        comment,
                    }
                );

                alert("Review updated!");
            } else {
                // ✅ CREATE
                await api.post("/reviews", {
                    booking_id: selectedBooking.id,
                    hotel_id: selectedBooking.hotel_id,
                    email: user.email,
                    rating,
                    comment
                });

                alert("Review submitted!");
            }

            setShowReviewModal(false);
            setRating(0);
            setComment("");

            fetchBookings(user.id); // 🔥 refresh UI

        } catch (err) {
            alert(err.response?.data?.message || "Error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm("Delete this review?")) return;

        try {
            await api.delete(`/reviews/${reviewId}`);

            alert("Review deleted");

            fetchBookings(user.id); // refresh

        } catch (err) {
            alert("Delete failed");
        }
    };

    const handleEditReview = (booking) => {
        setSelectedBooking(booking);
        setRating(booking.review.rating);
        setComment(booking.review.comment);
        setShowReviewModal(true);
    };

    const normalizeDate = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const isPastCheckout = (date) => {
        return normalizeDate(date) < normalizeDate(new Date());
    };

    const isFutureCheckIn = (date) => {
        return normalizeDate(date) > normalizeDate(new Date());
    };

    const getCancelDeadline = (booking) => {
        const createdAt = new Date(booking.created_at);
        const checkIn = new Date(booking.check_in);

        const freeCancelUntil = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000);

        return freeCancelUntil < checkIn ? freeCancelUntil : checkIn;
    };

    const canCancel = (booking) => {
        return new Date() < getCancelDeadline(booking);
    };

    const getCancelCountdown = (booking) => {
        const now = new Date();
        const deadline = getCancelDeadline(booking);

        const diff = deadline - now;

        if (diff <= 0) return null;

        const totalSeconds = Math.floor(diff / 1000);

        return {
            hours: Math.floor(totalSeconds / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60
        };
    };

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-white shadow-lg rounded-2xl p-8 text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">
                        Please Login
                    </h2>

                    <p className="text-gray-500 mb-5">
                        You must login to view your profile and bookings.
                    </p>

                    <button
                        onClick={onLoginClick}
                        className="inline-block bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700 text-white px-6 py-3 rounded-lg hover:shadow-lg transition"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-8">

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Sidebar */}
                <aside className="
            lg:col-span-3
            bg-white dark:bg-gray-900
            rounded-3xl
            p-5
            border border-gray-200 dark:border-gray-800
            shadow-lg
            h-fit
        ">

                    <div className="mb-6">
                        <h2 className="
                text-xl 
                font-bold 
                text-gray-900 
                dark:text-white
                ">
                            My Account
                        </h2>

                        <p className="text-sm text-gray-500 mt-1">
                            Manage your profile & bookings
                        </p>
                    </div>


                    <div className="space-y-3">


                        <button
                            onClick={() => setActiveTab("profile")}
                            className={`
                    w-full
                    flex
                    items-center
                    gap-3
                    px-4
                    py-3
                    rounded-xl
                    font-medium
                    transition

                    ${activeTab === "profile"
                                    ?
                                    "bg-gradient-to-r from-[#D9BB74] to-[#b89545] text-white shadow-lg"
                                    :
                                    "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                }
                    `}
                        >

                            👤
                            Profile

                        </button>



                        <button
                            onClick={() => setActiveTab("bookings")}
                            className={`
                    w-full
                    flex
                    items-center
                    gap-3
                    px-4
                    py-3
                    rounded-xl
                    font-medium
                    transition

                    ${activeTab === "bookings"
                                    ?
                                    "bg-gradient-to-r from-[#D9BB74] to-[#b89545] text-white shadow-lg"
                                    :
                                    "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                }
                    `}
                        >

                            🏨
                            My Bookings

                        </button>


                    </div>


                </aside>

                {/* Main Content */}
                <div className="lg:col-span-9">

                    {/* PROFILE TAB */}
                    {activeTab === "profile" && (

                        <div className="
bg-white
dark:bg-gray-900
rounded-3xl
border
border-gray-200
dark:border-gray-800
p-6
shadow-lg
">


                            <div className="mb-6">
                                <h1 className="
        text-3xl
        font-bold
        text-gray-900
        dark:text-white
        ">
                                    Profile Information
                                </h1>

                                <p className="
        text-sm
        text-gray-500
        dark:text-gray-400
        mt-2
        ">
                                    Update your personal information
                                </p>
                            </div>



                            <form
                                onSubmit={updateProfile}
                                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                            >


                                {[
                                    {
                                        name: "name",
                                        placeholder: "Full Name"
                                    },
                                    {
                                        name: "email",
                                        placeholder: "Email Address"
                                    },
                                    {
                                        name: "phone",
                                        placeholder: "Phone Number"
                                    },
                                    {
                                        name: "nationality",
                                        placeholder: "Nationality"
                                    }

                                ].map((field) => (

                                    <input
                                        key={field.name}
                                        name={field.name}
                                        value={form[field.name] || ""}
                                        onChange={handleChange}
                                        placeholder={field.placeholder}
                                        className="
            w-full
            px-4
            py-3
            rounded-xl
            border
            border-gray-300
            dark:border-gray-700
            bg-gray-50
            dark:bg-gray-800
            text-gray-900
            dark:text-white
            outline-none
            transition
            focus:ring-2
            focus:ring-[#D9BB74]
            "
                                    />

                                ))}



                                <button
                                    className="
        sm:col-span-2
        py-3
        rounded-xl
        text-white
        font-semibold
        bg-gradient-to-r
        from-[#D9BB74]
        to-[#b89545]
        hover:shadow-xl
        hover:scale-[1.02]
        transition
        "
                                >

                                    Update Profile

                                </button>


                            </form>


                        </div>

                    )}

                    {/* BOOKINGS TAB */}
                    {activeTab === "bookings" && (

                        <div className="
bg-white
dark:bg-gray-900
rounded-3xl
border
border-gray-200
dark:border-gray-800
p-6
shadow-lg
">


                            {/* HEADER */}

                            <div className="
flex
flex-col
md:flex-row
md:justify-between
md:items-center
gap-4
mb-8
">

                                <div>

                                    <h1 className="
text-3xl
font-bold
text-gray-900
dark:text-white
">
                                        My Bookings
                                    </h1>

                                    <p className="text-sm text-gray-500 mt-2">
                                        Manage reservations, cancellations and reviews.
                                    </p>

                                </div>


                                <div className="
px-5
py-3
rounded-2xl
bg-[#D9BB74]/10
border
border-[#D9BB74]/30
">

                                    <p className="text-xs text-gray-500 uppercase">
                                        Total Bookings
                                    </p>

                                    <p className="
text-2xl
font-bold
text-[#b89545]
">
                                        {bookings.length}
                                    </p>

                                </div>


                            </div>



                            {/* EMPTY */}

                            {bookings.length === 0 ? (

                                <div className="text-center py-20">

                                    <div className="text-5xl mb-4">
                                        🏨
                                    </div>

                                    <h3 className="
text-xl
font-semibold
text-gray-900
dark:text-white
">
                                        No bookings yet
                                    </h3>

                                    <p className="text-gray-500 mt-2">
                                        Your reservations will appear here.
                                    </p>

                                </div>


                            ) : (


                                <div className="space-y-6">


                                    {bookings.map((b) => (


                                        <div
                                            key={b.id}
                                            className="
rounded-3xl
border
border-gray-200
dark:border-gray-800
overflow-hidden
shadow-md
hover:shadow-xl
transition
"
                                        >


                                            {/* TOP */}

                                            <div className="
p-6
bg-gray-50
dark:bg-gray-800
flex
flex-col
md:flex-row
md:justify-between
gap-4
">


                                                <div>

                                                    <p className="text-xs text-gray-500 uppercase">
                                                        Reservation
                                                    </p>


                                                    <h3 className="
text-xl
font-bold
text-gray-900
dark:text-white
mt-1
">
                                                        {b.hotel_name}
                                                    </h3>


                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {b.room_name} • Room {b.room_number}
                                                    </p>


                                                </div>



                                                <span
                                                    className={`
px-4
py-2
rounded-full
h-fit
text-xs
font-semibold

${b.status === "confirmed"
                                                            ?
                                                            "bg-green-100 text-green-700"
                                                            :
                                                            b.status === "cancelled"
                                                                ?
                                                                "bg-red-100 text-red-700"
                                                                :
                                                                "bg-yellow-100 text-yellow-700"
                                                        }

`}
                                                >

                                                    {b.status}

                                                </span>


                                            </div>



                                            {/* DETAILS */}


                                            <div className="p-6">


                                                <div className="
grid
sm:grid-cols-2
lg:grid-cols-4
gap-4
">


                                                    {[
                                                        ["Check-in", formatDate(b.check_in)],
                                                        ["Check-out", formatDate(b.check_out)],
                                                        ["Guests", `${b.guests} Guests`],
                                                        ["Amount", `AED ${b.total_amount}`]

                                                    ].map((item) => (


                                                        <div
                                                            key={item[0]}
                                                            className="
p-4
rounded-2xl
bg-gray-50
dark:bg-gray-800
"
                                                        >

                                                            <p className="text-xs text-gray-500">
                                                                {item[0]}
                                                            </p>

                                                            <p className="
font-semibold
text-gray-900
dark:text-white
mt-1
">
                                                                {item[1]}
                                                            </p>

                                                        </div>


                                                    ))}


                                                </div>




                                                {/* ACTIONS */}


                                                <div className="
flex
flex-wrap
gap-3
mt-6
">


                                                    {b.status === "confirmed" && canCancel(b) && (

                                                        <>

                                                            <button
                                                                onClick={() => cancelBooking(b.id)}
                                                                disabled={cancelLoading === b.id}
                                                                className="
px-5
py-2.5
rounded-xl
bg-red-600
text-white
text-sm
font-medium
hover:bg-red-700
transition
"
                                                            >

                                                                {
                                                                    cancelLoading === b.id
                                                                        ?
                                                                        "Cancelling..."
                                                                        :
                                                                        "Cancel Booking"
                                                                }

                                                            </button>



                                                            {(() => {
                                                                const time = getCancelCountdown(b);

                                                                if (!time) return null;


                                                                return (

                                                                    <div className="
px-4
py-2
rounded-xl
bg-yellow-50
text-yellow-700
text-xs
font-medium
">

                                                                        Cancel in{" "}
                                                                        {String(time.hours).padStart(2, "0")}:
                                                                        {String(time.minutes).padStart(2, "0")}:
                                                                        {String(time.seconds).padStart(2, "0")}

                                                                    </div>

                                                                )

                                                            })()}


                                                        </>

                                                    )}





                                                    {b.status === "confirmed" &&
                                                        isPastCheckout(b.check_out) &&
                                                        !b.review && (

                                                            <button
                                                                onClick={() => openReviewModal(b)}
                                                                className="
px-5
py-2.5
rounded-xl
bg-[#D9BB74]
text-white
text-sm
font-medium
hover:bg-[#b89545]
transition
"
                                                            >
                                                                Leave Review
                                                            </button>

                                                        )}


                                                </div>



                                                {/* REVIEW */}


                                                {b.review && (

                                                    <div className="
mt-6
p-5
rounded-2xl
bg-gray-50
dark:bg-gray-800
">


                                                        <div className="
flex
justify-between
items-center
mb-3
">

                                                            <h4 className="
font-semibold
text-gray-900
dark:text-white
">
                                                                Your Review
                                                            </h4>


                                                            <span className="
bg-yellow-100
text-yellow-700
px-3
py-1
rounded-full
text-sm
">
                                                                ⭐ {b.review.rating}/5
                                                            </span>


                                                        </div>



                                                        <p className="
text-sm
text-gray-600
dark:text-gray-300
">
                                                            {b.review.comment}
                                                        </p>



                                                        <div className="flex gap-3 mt-4">


                                                            <button
                                                                onClick={() => handleEditReview(b)}
                                                                className="
px-4
py-2
rounded-xl
bg-blue-600
text-white
text-sm
"
                                                            >
                                                                Edit
                                                            </button>


                                                            <button
                                                                onClick={() => handleDeleteReview(b.review.id)}
                                                                className="
px-4
py-2
rounded-xl
bg-red-600
text-white
text-sm
"
                                                            >
                                                                Delete
                                                            </button>


                                                        </div>


                                                    </div>

                                                )}



                                            </div>


                                        </div>


                                    ))}


                                </div>

                            )}


                        </div>

                    )}
                </div>
            </div>

            {showReviewModal && (

                <div className="
fixed
inset-0
z-50
bg-black/60
flex
items-center
justify-center
px-4
">


                    <div className="
w-full
max-w-md
bg-white
dark:bg-gray-900
rounded-3xl
p-6
border
border-gray-200
dark:border-gray-800
shadow-2xl
">


                        {/* HEADER */}

                        <div className="mb-5">

                            <h2 className="
text-2xl
font-bold
text-gray-900
dark:text-white
">
                                Share Your Experience
                            </h2>


                            <p className="
text-sm
text-gray-500
dark:text-gray-400
mt-2
">
                                {selectedBooking?.hotel_name}
                            </p>


                        </div>




                        {/* STARS */}


                        <div className="
flex
gap-2
mb-5
">

                            {[1, 2, 3, 4, 5].map((star) => (


                                <button
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className={`
text-3xl
transition
hover:scale-110

${star <= rating
                                            ?
                                            "text-[#D9BB74]"
                                            :
                                            "text-gray-300 dark:text-gray-600"
                                        }

`}
                                >

                                    ★


                                </button>


                            ))}


                        </div>





                        {/* COMMENT */}


                        <textarea

                            value={comment}

                            onChange={(e) => setComment(e.target.value)}

                            placeholder="Write your experience..."

                            rows="4"

                            className="
w-full
p-4
rounded-2xl
border
border-gray-300
dark:border-gray-700
bg-gray-50
dark:bg-gray-800
text-gray-900
dark:text-white
outline-none
resize-none
focus:ring-2
focus:ring-[#D9BB74]
mb-5
"

                        />




                        {/* SUBMIT */}


                        <button

                            onClick={submitReview}

                            disabled={loading}

                            className="
w-full
py-3
rounded-xl
text-white
font-semibold
bg-gradient-to-r
from-[#D9BB74]
to-[#b89545]
hover:shadow-xl
transition
"

                        >

                            {
                                loading
                                    ?
                                    "Submitting..."
                                    :
                                    "Submit Review"
                            }


                        </button>





                        {/* CLOSE */}


                        <button

                            onClick={() => setShowReviewModal(false)}

                            className="
w-full
mt-3
py-3
rounded-xl
bg-gray-200
dark:bg-gray-800
text-gray-700
dark:text-gray-300
font-medium
hover:bg-gray-300
dark:hover:bg-gray-700
transition
"

                        >

                            Close

                        </button>



                    </div>


                </div>

            )}
        </div>
    );
}